// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateTime } from '../lib/utils';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Switch,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Popover,
  Autocomplete,
  Tooltip,
  Snackbar,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import StorageIcon from '@mui/icons-material/Storage';
import ChatIcon from '@mui/icons-material/Chat';
import MessageIcon from '@mui/icons-material/Message';
import BoltIcon from '@mui/icons-material/Bolt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DownloadIcon from '@mui/icons-material/Download';
import LabelIcon from '@mui/icons-material/Label';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import { useTheme } from '@mui/material/styles';
import { Header } from './Header';

// Tab content wrapper: no card chrome — inner cards provide their own surfaces
// (avoids cards-inside-a-card)
const tabWrapperSx = { bgcolor: 'transparent', border: 'none', boxShadow: 'none', borderRadius: 0 };

// Collapsible usage tree with a switchable ROOT dimension:
//   'assistant': Assistant → Provider → Model → User
//   'model':     Model → User            (provider as sub-label)
//   'provider':  Provider → Model → User
// Built from the flat llm-usage rows (each dimension summed at every level).
function UsageTree({ rows, groupBy = 'assistant' }) {
  const mk = () => ({ tokens: 0, calls: 0, input: 0, output: 0, children: {} });
  const add = (n, r) => {
    n.tokens += r.tokens; n.calls += r.calls;
    n.input += r.input_tokens; n.output += r.output_tokens;
  };

  const tree = mk();
  for (const r of rows) {
    const assistant = r.gpt_name || r.gpt_id;
    const provider = r.provider_name || r.provider;
    const model = r.model;
    const user = r.user_email || r.user_id;

    const chain =
      groupBy === 'model' ? [model, user]
      : groupBy === 'provider' ? [provider, model, user]
      : [assistant, provider, model, user];

    // Accumulate at EVERY level of the chain — parents must carry the sum
    // of their children (the leaf-only add left all parents at 0).
    add(tree, r);
    let n = tree;
    for (const k of chain) {
      n = (n.children[k] = n.children[k] || mk());
      add(n, r);
    }
    if (groupBy === 'model') {
      const m = tree.children[model];
      if (m && !m.sub) m.sub = provider;
    }
  }

  const [expanded, setExpanded] = useState(() => new Set(Object.keys(tree.children)));

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const sortedEntries = (obj) => Object.entries(obj).sort((a, b) => b[1].tokens - a[1].tokens);

  // Right-hand numeric columns, aligned across every row (header, nodes, grand total)
  const COLS = [
    { key: 'input', label: '↓ In', width: 120 },
    { key: 'output', label: '↑ Out', width: 100 },
    { key: 'tokens', label: 'Total', width: 120 },
    { key: 'calls', label: 'Calls', width: 70 },
  ];
  const Numbers = ({ node, bold, header }) => COLS.map((c) => (
    <Typography
      key={c.key}
      variant={header ? 'caption' : 'body2'}
      color={header || (!bold && c.key !== 'tokens') ? 'text.secondary' : 'text.primary'}
      fontWeight={bold || c.key === 'tokens' ? 600 : 400}
      sx={{ width: c.width, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
    >
      {header ? c.label : (node[c.key] || 0).toLocaleString()}
    </Typography>
  ));

  const Node = ({ label, sub, node, nodeKey, depth, children }) => {
    const isOpen = expanded.has(nodeKey);
    const hasChildren = children && children.length > 0;
    return (
      <Box>
        <Box
          onClick={() => hasChildren && toggle(nodeKey)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2,
            pl: 2 + depth * 3,
            cursor: hasChildren ? 'pointer' : 'default',
            '&:hover': hasChildren ? { bgcolor: 'action.hover' } : {},
            borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <Box sx={{ width: 20, fontSize: '0.9rem', color: 'text.secondary', textAlign: 'center', flexShrink: 0 }}>
            {hasChildren ? (isOpen ? '▾' : '▸') : '·'}
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={depth === 0 ? 600 : 400} noWrap sx={{ maxWidth: '100%' }}>
              {label}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Numbers node={node} />
        </Box>
        {isOpen && children}
      </Box>
    );
  };

  // Recursive renderer: children arrays are keyed paths so expansion survives
  // re-renders regardless of the grouping depth.
  const renderLevel = (node, keyPrefix, depth) =>
    sortedEntries(node.children).map(([name, child], i) => (
      <Node
        key={name}
        nodeKey={`${keyPrefix}/${name}`}
        node={child}
        label={name}
        sub={depth === 0 && groupBy === 'model' ? child.sub : undefined}
        depth={depth}
      >
        {renderLevel(child, `${keyPrefix}/${name}`, depth + 1)}
      </Node>
    ));

  const rowSx = { display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2 };
  return (
    <Box>
      <Box sx={{ ...rowSx, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }} />
        <Numbers header />
      </Box>
      {renderLevel(tree, '', 0)}
      <Box sx={{ ...rowSx, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="body2" fontWeight={700} sx={{ flex: 1, pl: '28px' }}>Grand total</Typography>
        <Numbers node={tree} bold />
      </Box>
    </Box>
  );
}

export default function CompanyAdmin({ isDarkMode }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [availableLLMProviders, setAvailableLLMProviders] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [llmUsage, setLlmUsage] = useState(null);
  const [usageFrom, setUsageFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 29);
    return d.toISOString().slice(0, 10);
  });
  const [usageTo, setUsageTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [usageGroupBy, setUsageGroupBy] = useState('assistant');

  // Modal states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [importUsersModalOpen, setImportUsersModalOpen] = useState(false);

  // Form states
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    name: '',
    email: '',
    subscription_tier: '',
    allowed_upload_domains: '',
    custom_css: '',
    lock_gpt_selection: false,
  });

  // LLM providers state
  const [llmProviders, setLlmProviders] = useState({});
  const [providerToAdd, setProviderToAdd] = useState('');
  const providersContainerRef = useRef(null);

  // Embedder state (dedicated embedding provider: Knowledge + Playbook)
  const [embeddingProvider, setEmbeddingProvider] = useState({
    provider: 'openai',
    api_key: '',
    model: 'text-embedding-3-small',
    enabled: true,
  });


  // Logo/Favicon upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', message: '' });
  const logoInputRef = useRef(null);
  const logoDarkInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const [companyGpts, setCompanyGpts] = useState([]);

  // User tags + bulk actions state
  const [userTags, setUserTags] = useState([]); // [{tag, count}]
  const [tagFilter, setTagFilter] = useState(() => new Set());
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [tagEditor, setTagEditor] = useState(null); // {userId, anchorEl}
  const [tagEditorValue, setTagEditorValue] = useState('');
  const [bulkDialog, setBulkDialog] = useState(null); // 'gpts' | 'role' | 'addtags' | 'removetags' | 'delete' | 'block' | 'unblock'
  const [bulkGptIds, setBulkGptIds] = useState(() => new Set());
  const [bulkRole, setBulkRole] = useState('user');
  const [bulkTagsValue, setBulkTagsValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [usersMessage, setUsersMessage] = useState({ type: '', message: '' });
  const [resendToast, setResendToast] = useState(null); // { message, severity }
  const [userSearch, setUserSearch] = useState('');
  const [allGpts, setAllGpts] = useState([]); // full GPT list (for per-user access view)
  const [accessPopover, setAccessPopover] = useState(null); // {userId, anchorEl}

  const getAuthToken = () => localStorage.getItem('cme_token');

  // Load LLM providers
  const loadLLMProviders = useCallback(async () => {
    const token = getAuthToken();
    try {
      const res = await fetch('/companies/config/llm-providers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const providers = data.providers || [];
        setAvailableLLMProviders(providers);
        return providers;
      }
    } catch (error) {
      console.error('Error loading LLM providers:', error);
      const fallback = [
        { id: 'openai', name: 'OpenAI' },
        { id: 'gemini', name: 'Google Gemini' },
        { id: 'deepseek', name: 'DeepSeek' },
        { id: 'claude', name: 'Anthropic Claude' },
      ];
      setAvailableLLMProviders(fallback);
      return fallback;
    }
    return [];
  }, []);

  // Load user tags catalog (with usage counts)
  const loadUserTags = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/user-tags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setUserTags(await res.json());
    } catch (error) {
      console.error('Error loading user tags:', error);
    }
  }, []);

  // Load the full GPT list (admins see all company GPTs) — used to show
  // which assistants each user can access
  useEffect(() => {
    if (activeTab !== 1 || !currentUser?.company_id || allGpts.length > 0) return;
    const token = getAuthToken();
    fetch(`/custom-gpts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setAllGpts(Array.isArray(data) ? data : []))
      .catch(() => setAllGpts([]));
  }, [activeTab, currentUser?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Usage tab: load the breakdown whenever the tab opens or the range changes
  useEffect(() => {
    if (activeTab !== 6 || !currentUser?.company_id) return;
    if (!usageFrom || !usageTo || usageFrom > usageTo) return;
    loadLlmUsage(currentUser.company_id, usageFrom, usageTo);
  }, [activeTab, currentUser?.company_id, usageFrom, usageTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load users
  const loadUsers = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
      loadUserTags(companyId);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, [loadUserTags]);

  // Set tags on one user (from the tag editor popover)
  const handleSetUserTags = async (userId, tags) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/users/${userId}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tags }),
      });
      if (res.ok) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, tags } : u)));
        if (currentUser?.company_id) loadUserTags(currentUser.company_id);
      }
    } catch (e) {
      console.error('Error setting user tags:', e);
    }
  };

  // Run a bulk action on the selected users
  const handleBulkAction = async (action, payload = {}) => {
    if (!selectedUserIds.size) return;
    setBulkBusy(true);
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/users/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_ids: Array.from(selectedUserIds), action, ...payload }),
      });
      if (res.ok) {
        const report = await res.json();
        setBulkDialog(null);
        setBulkGptIds(new Set());
        setBulkTagsValue('');
        setSelectedUserIds(new Set());
        setUsersMessage({
          type: 'success',
          message: `${action.replace('_', ' ')}: ${report.summary.ok} ok` +
            (report.summary.skipped ? `, ${report.summary.skipped} skipped` : ''),
        });
        if (currentUser?.company_id) loadUsers(currentUser.company_id);
      } else {
        const err = await res.json().catch(() => ({}));
        setUsersMessage({ type: 'error', message: err.detail || `Bulk ${action} failed` });
      }
    } catch (e) {
      setUsersMessage({ type: 'error', message: 'Bulk action failed' });
    } finally {
      setBulkBusy(false);
    }
  };

  // Open the "assign GPTs" bulk dialog (loads company GPTs if needed)
  const openBulkGptsDialog = async () => {
    setBulkGptIds(new Set());
    if (!companyGpts.length && currentUser?.company_id) {
      try {
        const res = await fetch(`/companies/${currentUser.company_id}/gpts`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (res.ok) setCompanyGpts(await res.json());
      } catch (e) { console.error(e); }
    }
    setBulkDialog('gpts');
  };

  // Revoke a user's specific access to a GPT (remove from shared_with_users)
  const handleRevokeGptAccess = async (gptId, userId) => {
    const gpt = allGpts.find((g) => g.id === gptId);
    if (!gpt) return;
    const updated = (gpt.shared_with_users || []).filter((id) => id !== userId);
    const token = getAuthToken();
    try {
      const res = await fetch(`/custom-gpts/${gptId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shared_with_users: updated }),
      });
      if (res.ok) {
        setAllGpts((prev) => prev.map((g) => (g.id === gptId ? { ...g, shared_with_users: updated } : g)));
      } else {
        const err = await res.json().catch(() => ({}));
        setUsersMessage({ type: 'error', message: err.detail || 'Failed to revoke access' });
      }
    } catch (e) {
      setUsersMessage({ type: 'error', message: 'Failed to revoke access' });
    }
  };

  // Load invitations
  const loadInvitations = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/invitations?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }, []);

  // Load statistics
  const loadStatistics = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  }, []);

  // Load LLM usage breakdown (dedicated endpoint, date-range aware)
  const loadLlmUsage = useCallback(async (companyId, from, to) => {
    const token = getAuthToken();
    try {
      const res = await fetch(
        `/companies/${companyId}/llm-usage?date_from=${from}&date_to=${to}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setLlmUsage(await res.json());
      }
    } catch (error) {
      console.error('Error loading LLM usage:', error);
    }
  }, []);

  // Export the current usage rows as CSV. 'sep=;' header line makes Excel
  // split on ; regardless of locale; quotes only where actually needed.
  const exportUsageCsv = () => {
    if (!llmUsage?.rows?.length) return;
    const header = ['Assistant', 'Provider', 'Model', 'User', 'Tokens', 'Input', 'Output', 'Calls'];
    const lines = llmUsage.rows.map((r) => [
      r.gpt_name || r.gpt_id,
      r.provider_name || r.provider,
      r.model,
      r.user_email || r.user_id,
      r.tokens, r.input_tokens, r.output_tokens, r.calls,
    ]);
    const cell = (c) => {
      const s = String(c ?? '');
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = '\ufeff' + 'sep=;\r\n' + [header, ...lines]
      .map((row) => row.map(cell).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `llm-usage_${llmUsage.from}_${llmUsage.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load company GPTs for export filter
  const loadCompanyGpts = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch('/custom-gpts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const gpts = await res.json();
        setCompanyGpts(gpts || []);
      }
    } catch (error) {
      console.error('Error loading GPTs:', error);
    }
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      const token = getAuthToken();
      if (!token) {
        window.location.href = '/';
        return;
      }

      try {
        const yamlProviders = await loadLLMProviders();

        // Get current user
        const userRes = await fetch('/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!userRes.ok) throw new Error('Failed to get user info');
        const user = await userRes.json();

        if (user.role !== 'company_admin' && user.role !== 'company_owner' && user.role !== 'super_admin') {
          alert('Access denied. This page is for company administrators only.');
          window.location.href = '/';
          return;
        }

        setCurrentUser(user);

        // Get company info
        if (user.company_id) {
          const companyRes = await fetch(`/companies/${user.company_id}/admin`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (companyRes.ok) {
            const company = await companyRes.json();
            setCurrentCompany(company);

            // Set document title and favicon
            document.title = `${company.name} - Admin`;
            if (company.favicon_url) {
              const faviconUrl = `${company.favicon_url}?t=${Date.now()}`;
              const existingFavicon = document.querySelector('link[rel="icon"]');
              if (existingFavicon) {
                existingFavicon.href = faviconUrl;
              } else {
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = faviconUrl;
                document.head.appendChild(link);
              }
            }

            // Set settings form values
            setSettings({
              name: company.name || '',
              email: company.email || '',
              subscription_tier: company.subscription_tier || 'free',
              allowed_upload_domains: (company.allowed_upload_domains || []).join('\n'),
              custom_css: company.custom_css || '',
              lock_gpt_selection: !!company.lock_gpt_selection,
            });

            // Set LLM providers as stored. available_models is INDICATIVE:
            // models can be added free-text ("Add model...") and must never
            // be filtered against config.yaml — a stale YAML list here used
            // to HIDE user-added models and, on save, silently DELETE them
            // from the provider (e.g. gemini-3.7-flash disappearing).
            if (company.settings?.providers) {
              setLlmProviders({ ...company.settings.providers });
            }

            // Set embedding provider
            if (company.settings?.embedding_provider) {
              setEmbeddingProvider(company.settings.embedding_provider);
            }

            // Load data
            await Promise.all([
              loadUsers(user.company_id),
              loadInvitations(user.company_id),
              loadStatistics(user.company_id),
              loadCompanyGpts(user.company_id),
            ]);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Initialization error:', error);
        alert('Failed to load page. Please try again.');
        window.location.href = '/';
      }
    };

    init();
  }, [loadLLMProviders, loadUsers, loadInvitations, loadStatistics, loadCompanyGpts]);

  const handleBlockUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to block ${userName}? They will no longer be able to access the system.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/block`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to block user');

      alert(`${userName} has been blocked successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user: ' + error.message);
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to unblock ${userName}?`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/unblock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to unblock user');

      alert(`${userName} has been unblocked successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user: ' + error.message);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to permanently remove ${userName}? This action cannot be undone.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to remove user');

      alert(`${userName} has been removed successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Failed to remove user: ' + error.message);
    }
  };

  const handleChangeRole = async (userId, userName, newRole) => {
    const roleLabels = {
      user: 'User',
      company_admin: 'Company Admin',
      company_owner: 'Company Owner',
    };

    if (!confirm(`Change ${userName}'s role to ${roleLabels[newRole]}?`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to change role');
      }

      alert(`${userName}'s role has been changed to ${roleLabels[newRole]}.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error changing role:', error);
      alert('Failed to change role: ' + error.message);
    }
  };

  const handleToggleCanCreateGpts = async (userId, currentValue) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/permissions`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ can_create_gpts: !currentValue }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update permission');
      }

      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error updating permission:', error);
      alert('Failed to update permission: ' + error.message);
    }
  };

  // Get available roles for role change based on current user's permissions
  const getAvailableRolesForChange = (targetUser) => {
    // Cannot change own role or super_admin roles
    if (targetUser.id === currentUser?.id || targetUser.role === 'super_admin') {
      return [];
    }

    const allRoles = [
      { value: 'user', label: 'User' },
      { value: 'company_admin', label: 'Company Admin' },
      { value: 'company_owner', label: 'Company Owner' },
    ];

    let availableRoles = [];

    // Super admin and company owner can assign all roles
    if (currentUser?.role === 'super_admin' || currentUser?.role === 'company_owner') {
      availableRoles = [...allRoles];
    }
    // Company admin can only assign user or admin (not owner)
    else if (currentUser?.role === 'company_admin') {
      // Admin cannot change owners at all
      if (targetUser.role === 'company_owner') {
        return [];
      }
      // Admin can only assign user or admin
      availableRoles = allRoles.filter((r) => r.value !== 'company_owner');
    }

    // Filter out the target user's current role (no point changing to the same role)
    return availableRoles.filter((r) => r.value !== targetUser.role);
  };

  const handleResendInvitation = async (invitationId, email) => {
    // No confirmation — the button just does it and reports via toast
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${currentUser.company_id}/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to resend invitation');
      }

      const result = await res.json();
      if (result.email_sent) {
        setResendToast({ message: `Invitation resent to ${email} — expires in 7 days`, severity: 'success' });
      } else {
        const fullUrl = `${window.location.origin}/accept-invite.html?token=${result.token}`;
        navigator.clipboard.writeText(fullUrl).catch(() => {});
        setResendToast({ message: 'Email service not configured — invite link copied to clipboard', severity: 'warning' });
      }
      // Refresh so the reset expiration date is visible
      loadInvitations(currentUser.company_id);
    } catch (error) {
      console.error('Error resending invitation:', error);
      setResendToast({ message: 'Failed to resend invitation: ' + error.message, severity: 'error' });
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${currentUser.company_id}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to cancel invitation');

      loadInvitations(currentUser.company_id);
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Failed to cancel invitation');
    }
  };

  const handleSaveSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      const allowedDomains = settings.allowed_upload_domains
        .split('\n')
        .map(d => d.trim())
        .filter(d => d.length > 0);

      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: settings.name,
          email: settings.email,
          allowed_upload_domains: allowedDomains,
          custom_css: settings.custom_css,
          lock_gpt_selection: !!settings.lock_gpt_selection,
        }),
      });

      if (!res.ok) throw new Error('Failed to save settings');

      const company = await res.json();
      setCurrentCompany(company);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to extract model IDs from supported_models (handles both object and string formats)
  const extractModelIds = (supportedModels) => {
    if (!supportedModels) return [];
    return supportedModels.map((m) => (typeof m === 'object' ? m.id : m));
  };

  // Helper to get full model info from config (for checkbox display)
  const getProviderModels = (providerId) => {
    const providerConfig = availableLLMProviders.find((p) => p.id === providerId);
    if (!providerConfig?.supported_models) return [];
    return providerConfig.supported_models.map((m) => {
      if (typeof m === 'object') {
        return { id: m.id, supports_tools: m.supports_tools !== false };
      }
      return { id: m, supports_tools: true }; // backward compat: strings support tools
    });
  };

  // Toggle model selection
  const handleModelToggle = (providerKey, modelId, isChecked) => {
    const currentModels = llmProviders[providerKey]?.available_models || [];
    let newModels;
    if (isChecked) {
      newModels = [...currentModels, modelId];
    } else {
      newModels = currentModels.filter((m) => m !== modelId);
    }
    handleProviderChange(providerKey, 'available_models', newModels);
  };

  // LLM Provider handlers
  const handleAddProvider = () => {
    if (!providerToAdd) return;

    const providerConfig = availableLLMProviders.find((p) => p.id === providerToAdd);
    const modelIds = extractModelIds(providerConfig?.supported_models);
    let providerKey = providerToAdd;
    let counter = 1;
    while (llmProviders[providerKey]) {
      counter++;
      providerKey = `${providerToAdd}_${counter}`;
    }

    // Check if this is a custom provider
    const isCustomProvider = providerToAdd === 'custom' || providerConfig?.is_custom;

    // Base config for all providers
    const newConfig = {
      name: providerConfig?.name || providerToAdd,
      provider: providerToAdd,
      enabled: true,
      api_key: '',
      default_model: isCustomProvider ? '' : (modelIds[0] || ''),
      available_models: isCustomProvider ? [] : modelIds,
      embedding_model: providerConfig?.embedding_models?.[0] || '',
      is_default: Object.keys(llmProviders).length === 0,
    };

    // Add custom provider specific fields
    if (isCustomProvider) {
      newConfig.base_url = '';
      newConfig.supports_tools = true;
      newConfig.supports_vision = false;
    }

    setLlmProviders((prev) => ({
      ...prev,
      [providerKey]: newConfig,
    }));
    setProviderToAdd('');

    setTimeout(() => {
      if (providersContainerRef.current) {
        const lastChild = providersContainerRef.current.lastElementChild;
        if (lastChild) {
          lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const handleRemoveProvider = (key) => {
    setLlmProviders((prev) => {
      const newProviders = { ...prev };
      delete newProviders[key];
      return newProviders;
    });
  };

  const handleProviderChange = (key, field, value) => {
    setLlmProviders((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSetDefault = (key) => {
    setLlmProviders((prev) => {
      const newProviders = {};
      Object.keys(prev).forEach((k) => {
        newProviders[k] = { ...prev[k], is_default: k === key };
      });
      return newProviders;
    });
  };

  const handleSaveLLMSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      // Strip UI-only editing buffers before sending (raw JSON drafts).
      const cleanedProviders = Object.fromEntries(
        Object.entries(llmProviders).map(([k, cfg]) => {
          const clean = { ...cfg };
          Object.keys(clean).forEach((f) => { if (f.startsWith('_raw_')) delete clean[f]; });
          return [k, clean];
        })
      );

      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            providers: Object.keys(cleanedProviders).length > 0 ? cleanedProviders : null,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to save LLM settings');

      alert('LLM configuration saved successfully!');
    } catch (error) {
      console.error('Error saving LLM settings:', error);
      alert('Failed to save LLM settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Save embedding provider settings
  const handleSaveEmbeddingSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            embedding_provider: embeddingProvider,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to save Embedder settings');

      alert('Embedder configuration saved successfully!');
    } catch (error) {
      console.error('Error saving Embedder settings:', error);
      alert('Failed to save Embedder settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Logo upload handler — variant 'light' (default logo) or 'dark' (shown in dark theme)
  const handleLogoUpload = (variant) => async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const inputRef = variant === 'dark' ? logoDarkInputRef : logoInputRef;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadMessage({ type: 'error', message: 'Logo file size must be less than 5MB' });
      return;
    }

    const token = getAuthToken();
    setLogoUploading(variant);
    setUploadMessage({ type: '', message: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`/companies/${currentUser.company_id}/logo?variant=${variant}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to upload logo');
      }

      const result = await res.json();
      setCurrentCompany(result.company);
      setUploadMessage({ type: 'success', message: variant === 'dark' ? 'Dark theme logo uploaded successfully!' : 'Logo uploaded successfully!' });

      // Clear file input
      if (inputRef.current) inputRef.current.value = '';
    } catch (error) {
      console.error('Logo upload error:', error);
      setUploadMessage({ type: 'error', message: `Failed to upload logo: ${error.message}` });
    } finally {
      setLogoUploading(false);
    }
  };

  // Favicon upload handler
  const handleFaviconUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      setUploadMessage({ type: 'error', message: 'Favicon file size must be less than 500KB' });
      return;
    }

    const token = getAuthToken();
    setFaviconUploading(true);
    setUploadMessage({ type: '', message: '' });

    try {
      const formData = new FormData();
      formData.append('favicon', file);

      const res = await fetch(`/companies/${currentUser.company_id}/favicon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to upload favicon');
      }

      const result = await res.json();
      setCurrentCompany(result.company);
      setUploadMessage({ type: 'success', message: 'Favicon uploaded successfully!' });

      // Clear file input
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    } catch (error) {
      console.error('Favicon upload error:', error);
      setUploadMessage({ type: 'error', message: `Failed to upload favicon: ${error.message}` });
    } finally {
      setFaviconUploading(false);
    }
  };

  const isImpersonating = localStorage.getItem('cme_impersonating') === 'true';

  const handleReturnToSuperAdmin = () => {
    const originalToken = localStorage.getItem('cme_original_token');
    if (originalToken) {
      localStorage.setItem('cme_token', originalToken);
      localStorage.removeItem('cme_original_token');
      localStorage.removeItem('cme_impersonating');
      window.location.href = '/super-admin.html';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Header
        company={currentCompany}
        isImpersonating={isImpersonating}
        onReturnToSuperAdmin={handleReturnToSuperAdmin}
        userRole={currentUser?.role}
        contentMaxWidth="1400px"
        contentPadding="24px"
        logoMinWidth="1990px"
      />

      <Box className="admin-container">
        {/* Page Header */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Company Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, settings, and view company statistics
          </Typography>
        </Box>
        <Divider sx={{ mb: 4 }} />

        {/* User Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Your Account
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Username</Typography>
                <Typography fontWeight={500}>{currentUser?.username}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography fontWeight={500}>{currentUser?.email}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Chip
                  label={currentUser?.role?.replace('_', ' ').toUpperCase()}
                  size="small"
                  color={
                    currentUser?.role === 'company_owner' ? 'secondary' :
                    currentUser?.role === 'company_admin' ? 'primary' : 'default'
                  }
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
              <Tab label="Statistics" icon={<BarChartIcon />} iconPosition="start" />
              <Tab label="Users" icon={<PeopleIcon />} iconPosition="start" />
              <Tab label="Invitations" icon={<PersonAddIcon />} iconPosition="start" />
              <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
              <Tab label="LLM Configuration" icon={<SmartToyIcon />} iconPosition="start" />
              <Tab label="Embedder" icon={<DataObjectIcon />} iconPosition="start" />
              <Tab label="Usage" icon={<BoltIcon />} iconPosition="start" />
        </Tabs>

        {/* Statistics Tab */}
        {activeTab === 0 && statistics && (
          <Box>
            {/* Stats Cards - Gentle styling with circular pastel icons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 26, color: '#3b82f6' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Total Users</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.users.total}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.users.limit} limit</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 26, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Active Users (7d)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.users.active_last_7_days}</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 26, color: '#a855f7' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Conversations (Month)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{(statistics.threads.this_month ?? 0).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Last month: {(statistics.threads.last_month ?? 0).toLocaleString()} · All-time: {statistics.threads.total.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(14, 165, 233, 0.2)' : '#e0f2fe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <MessageIcon sx={{ fontSize: 26, color: '#0ea5e9' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Messages (Month)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{(statistics.messages.this_month ?? 0).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Last month: {(statistics.messages.last_month ?? 0).toLocaleString()} · All-time: {statistics.messages.total.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 26, color: '#f59e0b' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Tokens (Month)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.tokens.total_this_month.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {statistics.tokens.limit_per_month
                        ? `${statistics.tokens.percentage_used?.toFixed(1) || 0}% of ${statistics.tokens.limit_per_month.toLocaleString()}`
                        : 'Unlimited'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 26, color: '#ef4444' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Assistants</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.custom_gpts.total}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.custom_gpts.limit} limit</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(107, 114, 128, 0.2)' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <StorageIcon sx={{ fontSize: 26, color: '#6b7280' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Storage Used</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.storage.used_mb} MB</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.storage.limit_mb} MB ({statistics.storage.percentage_used}%)</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fce7f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 26, color: '#ec4899' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Avg Msgs/Thread</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.messages.average_per_thread}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Top Users & GPTs */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4 }}>
              <Card sx={{ flex: 1, minWidth: 300 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Active Users</Typography>
                  {statistics.top_users.by_messages.length === 0 ? (
                    <Typography color="text.secondary">No activity yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {statistics.top_users.by_messages.map((user, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Box>
                              <Typography fontWeight={500}>{user.display_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{user.message_count}</Typography>
                            <Typography variant="caption" color="text.secondary">messages</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 300 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Used Assistants</Typography>
                  {statistics.custom_gpts.most_used.length === 0 ? (
                    <Typography color="text.secondary">No assistants used yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {statistics.custom_gpts.most_used.map((gpt, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Typography fontWeight={500}>{gpt.name}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{gpt.thread_count}</Typography>
                            <Typography variant="caption" color="text.secondary">conversations</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Usage Tab — LLM usage from the llm_usage_daily aggregate */}
        {activeTab === 6 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                LLM Usage
                {llmUsage?.total_tokens > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {llmUsage.total_tokens.toLocaleString()} tokens
                    (↓ {llmUsage.rows.reduce((s, r) => s + (r.input_tokens || 0), 0).toLocaleString()} / ↑ {llmUsage.rows.reduce((s, r) => s + (r.output_tokens || 0), 0).toLocaleString()})
                    · {new Set(llmUsage.rows.map((r) => r.user_email || r.user_id)).size} user(s)
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { id: 'assistant', label: 'By Assistant' },
                  { id: 'provider', label: 'By Provider' },
                  { id: 'model', label: 'By Model' },
                ].map((g) => (
                  <Button
                    key={g.id}
                    size="small"
                    variant={usageGroupBy === g.id ? 'contained' : 'outlined'}
                    onClick={() => setUsageGroupBy(g.id)}
                  >
                    {g.label}
                  </Button>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { label: 'Today', days: 1 },
                  { label: '7d', days: 7 },
                ].map((p) => (
                  <Button
                    key={p.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const to = new Date();
                      const from = new Date(); from.setDate(from.getDate() - (p.days - 1));
                      setUsageFrom(from.toISOString().slice(0, 10));
                      setUsageTo(to.toISOString().slice(0, 10));
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
                <TextField
                  size="small" type="date" label="From" value={usageFrom}
                  onChange={(e) => setUsageFrom(e.target.value)}
                  sx={{ width: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small" type="date" label="To" value={usageTo}
                  onChange={(e) => setUsageTo(e.target.value)}
                  sx={{ width: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  size="small" variant="contained" startIcon={<DownloadIcon />}
                  onClick={exportUsageCsv}
                  disabled={!llmUsage?.rows?.length}
                >
                  CSV
                </Button>
              </Box>
            </Box>

            {!llmUsage ? (
              <Typography color="text.secondary">Loading usage…</Typography>
            ) : llmUsage.total_tokens === 0 ? (
              <Typography color="text.secondary">
                No usage recorded between {llmUsage.from} and {llmUsage.to}.
              </Typography>
            ) : (
              <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <UsageTree rows={llmUsage.rows} groupBy={usageGroupBy} />
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Users Tab */}
        {activeTab === 1 && (() => {
          const q = userSearch.trim().toLowerCase();
          const filteredUsers = users.filter((u) => {
            const matchesTags = tagFilter.size === 0
              || Array.from(tagFilter).every((t) => (u.tags || []).includes(t));
            if (!matchesTags) return false;
            if (!q) return true;
            return (
              (u.email || '').toLowerCase().includes(q) ||
              (u.display_name || '').toLowerCase().includes(q)
            );
          });
          const selectable = filteredUsers.filter((u) => u.role !== 'super_admin');
          const allSelected = selectable.length > 0 && selectable.every((u) => selectedUserIds.has(u.id));
          const someSelected = selectable.some((u) => selectedUserIds.has(u.id)) && !allSelected;

          return (
          <Card sx={tabWrapperSx}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" fontWeight={600}>Company Users</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setImportUsersModalOpen(true)}
                    sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Import from Excel
                    </Box>
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteModalOpen(true)}
                    sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Invite User
                    </Box>
                  </Button>
                </Box>
              </Box>

              {usersMessage.message && (
                <Alert
                  severity={usersMessage.type || 'info'}
                  sx={{ mb: 2 }}
                  onClose={() => setUsersMessage({ type: '', message: '' })}
                >
                  {usersMessage.message}
                </Alert>
              )}

              {/* Filters row: user search (email/name) + tag chips, together on the left */}
              {users.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search email or name…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 230 } }}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                      ),
                    }}
                  />
                  {userTags.length > 0 && (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size="small"
                      options={userTags.map((t) => t.tag)}
                      value={Array.from(tagFilter)}
                      onChange={(e, newValue) => setTagFilter(new Set(newValue))}
                      sx={{ width: { xs: '100%', sm: 220 } }}
                      renderTags={(value) => value.length > 0 ? (
                        <Chip size="small" color="primary" label={`${value.length} selected`} />
                      ) : null}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        const count = userTags.find((t) => t.tag === option)?.count;
                        return (
                          <Box component="li" key={key} {...optionProps}>
                            <Checkbox size="small" checked={selected} sx={{ mr: 0.5, p: 0.25 }} />
                            <Typography variant="body2" noWrap>
                              {option}{count != null ? ` (${count})` : ''}
                            </Typography>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Filter by tags" />
                      )}
                    />
                  )}
                  {tagFilter.size > 0 && (
                    <Chip
                      size="small"
                      label="Clear filter"
                      variant="text"
                      onClick={() => setTagFilter(new Set())}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              )}

              {/* Bulk bar — appears when users are selected */}
              {selectedUserIds.size > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    p: 1.5,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    bgcolor: 'action.selected',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {selectedUserIds.size} selected
                  </Typography>
                  <Button size="small" startIcon={<LibraryAddCheckIcon />} onClick={openBulkGptsDialog}>
                    Assign GPTs
                  </Button>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setBulkDialog('role')}>
                    Change Role
                  </Button>
                  <Button size="small" startIcon={<LabelIcon />} onClick={() => setBulkDialog('addtags')}>
                    Add Tags
                  </Button>
                  <Button size="small" startIcon={<LabelIcon />} onClick={() => setBulkDialog('removetags')}>
                    Remove Tags
                  </Button>
                  <Button size="small" color="warning" startIcon={<BlockIcon />} onClick={() => setBulkDialog('block')}>
                    Block
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setBulkDialog('delete')}>
                    Remove
                  </Button>
                  <Button size="small" variant="text" onClick={() => setSelectedUserIds(new Set())}>
                    Clear selection
                  </Button>
                </Box>
              )}

              {/* Select all (respects the active tag filter) */}
              {users.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => setSelectedUserIds((prev) => {
                        const next = new Set(prev);
                        if (allSelected) selectable.forEach((u) => next.delete(u.id));
                        else selectable.forEach((u) => next.add(u.id));
                        return next;
                      })}
                    />
                  }
                  label={`Select all${tagFilter.size > 0 ? ' (filtered)' : ''} — ${filteredUsers.length} users`}
                  sx={{ mb: 1 }}
                />
              )}

              {users.length === 0 ? (
                <Typography color="text.secondary">No users found</Typography>
              ) : filteredUsers.length === 0 ? (
                <Typography color="text.secondary">
                  No users match the current filters (search{tagFilter.size > 0 ? ' + tags' : ''})
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {filteredUsers.map((user) => {
                    const isCurrentUser = user.id === currentUser?.id;
                    const isSuperAdmin = user.role === 'super_admin';
                    const canManage = !isCurrentUser && !isSuperAdmin;

                    return (
                      <Box
                        key={user.id}
                        sx={{
                          p: { xs: 1.25, sm: 2 },
                          border: '1px solid',
                          borderColor: selectedUserIds.has(user.id) ? 'primary.main' : 'divider',
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {!isSuperAdmin && (
                            <Checkbox
                              size="small"
                              sx={{ mt: -0.5, ml: -1 }}
                              checked={selectedUserIds.has(user.id)}
                              onChange={() => setSelectedUserIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(user.id)) next.delete(user.id); else next.add(user.id);
                                return next;
                              })}
                            />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Header: name (truncates) + role, status pinned right */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Typography fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                                {user.display_name || user.email}
                              </Typography>
                              <Chip
                                label={user.role.replace('_', ' ').toUpperCase()}
                                size="small"
                                color={
                                  user.role === 'company_owner' ? 'secondary' :
                                  user.role === 'company_admin' ? 'primary' : 'default'
                                }
                                sx={{ height: 22, flexShrink: 0, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                              />
                              {isCurrentUser && (
                                <Chip label="You" size="small" variant="outlined" sx={{ height: 22, flexShrink: 0 }} />
                              )}
                              <Chip
                                label={user.is_active ? 'Active' : 'Blocked'}
                                size="small"
                                variant="outlined"
                                color={user.is_active ? 'success' : 'error'}
                                sx={{ height: 22, flexShrink: 0, ml: 'auto' }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {user.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.last_activity
                                ? `Last activity: ${formatDateTime(user.last_activity)}`
                                : user.last_login
                                  ? `Last login: ${formatDateTime(user.last_login)}`
                                  : 'Never logged in'}
                            </Typography>
                            {/* Meta: tags + assistants as compact dropdowns */}
                            <Box
                              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, alignItems: 'center' }}
                            >
                              {(currentUser?.role === 'company_admin' || currentUser?.role === 'company_owner' || currentUser?.role === 'super_admin') ? (
                                <Chip
                                  icon={<LabelIcon sx={{ fontSize: '13px !important' }} />}
                                  label={user.tags?.length ? `${user.tags.length} tag${user.tags.length > 1 ? 's' : ''}` : 'Add tags'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, cursor: 'pointer' }}
                                  onClick={(e) => setTagEditor({ userId: user.id, anchorEl: e.currentTarget })}
                                />
                              ) : (
                                (user.tags || []).map((t) => (
                                  <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 22 }} />
                                ))
                              )}
                              {/* Assistants this user can access — dropdown */}
                              <Chip
                                icon={<SmartToyIcon sx={{ fontSize: '13px !important' }} />}
                                label={`${allGpts.filter((g) =>
                                  g.is_shared || g.user_id === user.id || (g.shared_with_users || []).includes(user.id)
                                ).length} assistants`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, cursor: 'pointer' }}
                                onClick={(e) => setAccessPopover({ userId: user.id, anchorEl: e.currentTarget })}
                              />
                            </Box>

                            {/* Actions — one compact line under the info */}
                            {canManage && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  mt: 1.25,
                                  pt: 1.25,
                                  borderTop: '1px dashed',
                                  borderColor: 'divider',
                                }}
                              >
                                {/* Can Create GPTs toggle - only for regular users (admins/owners always can) */}
                                {user.role === 'user' && (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={user.can_create_gpts || false}
                                        onChange={() => handleToggleCanCreateGpts(user.id, user.can_create_gpts)}
                                      />
                                    }
                                    label="Can create assistants"
                                    sx={{ mr: 0 }}
                                  />
                                )}
                                {/* Role Change Dropdown */}
                                {getAvailableRolesForChange(user).length > 0 && (
                                  <FormControl size="small" sx={{ width: { xs: 168, sm: 180 } }}>
                                    <Select
                                      value=""
                                      displayEmpty
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleChangeRole(user.id, user.display_name || user.email, e.target.value);
                                        }
                                      }}
                                      renderValue={() => (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                          <EditIcon fontSize="small" />
                                          <span>Change Role</span>
                                        </Box>
                                      )}
                                    >
                                      {getAvailableRolesForChange(user).map((role) => (
                                        <MenuItem key={role.value} value={role.value}>
                                          {role.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                                {user.is_active ? (
                                  <Tooltip title="Block user">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => handleBlockUser(user.id, user.display_name || user.email)}
                                    >
                                      <BlockIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Unblock user">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleUnblockUser(user.id, user.display_name || user.email)}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Remove user">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveUser(user.id, user.display_name || user.email)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
          );
        })()}

        {/* Invitations Tab */}
        {activeTab === 2 && (
          <Card sx={tabWrapperSx}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Pending Invitations</Typography>
                <Typography variant="caption" color="text.secondary">
                  Invite users from the Users tab
                </Typography>
              </Box>

              {invitations.length === 0 ? (
                <Typography color="text.secondary">No pending invitations</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {invitations.map((inv) => (
                    <Box
                      key={inv.id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={600}>{inv.email}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {inv.role.replace('_', ' ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {new Date(inv.expires_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<SendIcon />}
                          onClick={() => handleResendInvitation(inv.id, inv.email)}
                        >
                          Resend
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancelInvitation(inv.id)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 3 && (
          <Card sx={tabWrapperSx}>
            <CardContent sx={{ p: 0 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>Company Settings</Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings.name}
                  onChange={(e) => setSettings((prev) => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  fullWidth
                  label="Company Email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings((prev) => ({ ...prev, email: e.target.value }))}
                />
                <TextField
                  fullWidth
                  label="Subscription Tier"
                  value={settings.subscription_tier}
                  disabled
                  helperText="Contact support to change subscription tier"
                />
                <TextField
                  fullWidth
                  label="Allowed Upload Domains"
                  value={settings.allowed_upload_domains}
                  onChange={(e) => setSettings((prev) => ({ ...prev, allowed_upload_domains: e.target.value }))}
                  multiline
                  rows={3}
                  placeholder="yourdomain.com&#10;your-bucket.s3.amazonaws.com"
                  helperText="One domain per line. These domains are trusted for URL-based document uploads."
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.lock_gpt_selection}
                      onChange={(e) => setSettings((prev) => ({ ...prev, lock_gpt_selection: e.target.checked }))}
                    />
                  }
                  label="Lock assistant selection"
                  sx={{ alignSelf: 'flex-start' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                  Chats always start on the default assistant and the assistant chip is informative only.
                  Users can still open a specific assistant explicitly from the Assistants page ("Use").
                </Typography>
                <TextField
                  fullWidth
                  label="Custom CSS"
                  value={settings.custom_css}
                  onChange={(e) => setSettings((prev) => ({ ...prev, custom_css: e.target.value }))}
                  multiline
                  rows={6}
                  placeholder="nav.bg-white { background-color: #your-color; }"
                  helperText="Add custom CSS to style your pages."
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>

              {/* Company Logo Section */}
              <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Company Logo</Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {/* Logo Preview */}
                  <Box
                    sx={{
                      width: 128,
                      height: 128,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                    }}
                  >
                    {currentCompany?.logo_url ? (
                      <img
                        src={`${currentCompany.logo_url}?t=${Date.now()}`}
                        alt="Company logo"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">No logo</Typography>
                    )}
                  </Box>

                  {/* Upload Form */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Upload Logo</Typography>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload('light')}
                      style={{ display: 'none' }}
                      id="logo-upload-input"
                    />
                    <label htmlFor="logo-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={logoUploading === 'light' ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={!!logoUploading}
                      >
                        {logoUploading === 'light' ? 'Uploading...' : 'Choose File'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Supported formats: PNG, JPG, GIF, WebP (max 5MB, recommended: 500x500px)
                    </Typography>
                  </Box>
                </Box>

                {/* Dark theme variant */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3 }}>Logo for dark theme (optional)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Shown when users switch to dark mode. Upload a version with light text or colors; without it, the standard logo is used.
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 128,
                      height: 128,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#121212',
                      overflow: 'hidden',
                    }}
                  >
                    {currentCompany?.logo_dark_url ? (
                      <img
                        src={`${currentCompany.logo_dark_url}?t=${Date.now()}`}
                        alt="Company logo for dark theme"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.500' }}>No dark logo</Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Upload Dark Theme Logo</Typography>
                    <input
                      ref={logoDarkInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload('dark')}
                      style={{ display: 'none' }}
                      id="logo-dark-upload-input"
                    />
                    <label htmlFor="logo-dark-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={logoUploading === 'dark' ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={!!logoUploading}
                      >
                        {logoUploading === 'dark' ? 'Uploading...' : 'Choose File'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Supported formats: PNG, JPG, GIF, WebP (max 5MB). Use a transparent background.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Company Favicon Section */}
              <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Company Favicon</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The favicon is the small icon displayed in browser tabs and bookmarks
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {/* Favicon Preview */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                    }}
                  >
                    {currentCompany?.favicon_url ? (
                      <img
                        src={`${currentCompany.favicon_url}?t=${Date.now()}`}
                        alt="Favicon"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">No favicon</Typography>
                    )}
                  </Box>

                  {/* Upload Form */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Upload Favicon</Typography>
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/png,image/x-icon,.ico,.png"
                      onChange={handleFaviconUpload}
                      style={{ display: 'none' }}
                      id="favicon-upload-input"
                    />
                    <label htmlFor="favicon-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={faviconUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                        disabled={faviconUploading}
                      >
                        {faviconUploading ? 'Uploading...' : 'Choose File'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Supported formats: PNG, ICO (max 500KB, recommended: 32x32px or 64x64px)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Upload Message */}
              {uploadMessage.message && (
                <Alert severity={uploadMessage.type === 'error' ? 'error' : 'success'} sx={{ mt: 3 }}>
                  {uploadMessage.message}
                </Alert>
              )}

              {/* Save bar — at the bottom, where users expect it */}
              <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSaveSettings}
                  disabled={submitting}
                  startIcon={submitting ? null : <SaveIcon />}
                  sx={{ minWidth: 220, py: 1.25, fontWeight: 600, alignSelf: 'flex-start' }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save Settings'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Saves name, email, upload domains, assistant lock and custom CSS. Logos and favicon are saved automatically on upload.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* LLM Configuration Tab */}
        {activeTab === 4 && (
          <Card sx={tabWrapperSx}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>LLM Provider Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure the AI models and providers for your company
                </Typography>
              </Box>

              {/* LLM Providers Section */}
              <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1.5, sm: 0 },
                  mb: 2
                }}>
                  <Typography variant="h6" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    LLM Providers
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
                      <Select
                        value={providerToAdd}
                        onChange={(e) => setProviderToAdd(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Select provider...</MenuItem>
                        {availableLLMProviders.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddProvider}
                      startIcon={<AddIcon />}
                      sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1.5, sm: 2 } }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Add
                      </Box>
                    </Button>
                  </Box>
                </Box>

                {Object.keys(llmProviders).length === 0 ? (
                  <Typography color="text.secondary" fontStyle="italic">
                    No providers configured. Select a provider above and click "Add" to configure one.
                  </Typography>
                ) : (
                  <Box ref={providersContainerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(llmProviders).map(([key, config]) => (
                      <Box
                        key={key}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: config.is_default
                            ? (isDarkMode ? 'rgba(37, 99, 235, 0.15)' : '#eff6ff')
                            : (isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                          border: '1px solid',
                          borderColor: config.is_default
                            ? (isDarkMode ? 'rgba(147, 197, 253, 0.5)' : '#93c5fd')
                            : 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {config.name || key}
                              {' '}
                              <Typography component="span" variant="subtitle1" color="text.secondary" fontWeight={400}>
                                ({availableLLMProviders.find(p => p.id === config.provider)?.name || config.provider || key.split('_')[0]})
                              </Typography>
                            </Typography>
                            {config.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{ bgcolor: '#2563eb', color: 'white', fontSize: '0.75rem', height: 22 }}
                              />
                            )}
                          </Box>
                          <IconButton size="small" color="error" onClick={() => handleRemoveProvider(key)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.25, sm: 2 } }}>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={config.enabled}
                                  onChange={(e) => handleProviderChange(key, 'enabled', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Enabled</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={config.is_default}
                                  onChange={() => handleSetDefault(key)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Set as Default</Typography>}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Name</Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={config.name || ''}
                              onChange={(e) => handleProviderChange(key, 'name', e.target.value)}
                              placeholder="e.g., Production, Development, Testing"
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Custom name shown to users when creating assistants
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                              API Key {config.provider === 'custom' && <Typography component="span" variant="caption" color="text.secondary">(optional for local models)</Typography>}
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              type="password"
                              value={config.api_key || ''}
                              onChange={(e) => handleProviderChange(key, 'api_key', e.target.value)}
                              placeholder={config.provider === 'custom' ? 'Optional - leave empty for local models' : 'API key'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                          </Box>

                          {/* Custom Provider Fields */}
                          {config.provider === 'custom' && (
                            <>
                              <Box>
                                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Base URL *</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={config.base_url || ''}
                                  onChange={(e) => handleProviderChange(key, 'base_url', e.target.value)}
                                  placeholder="http://localhost:11434 (Ollama), http://localhost:8000 (vLLM)"
                                  sx={{ bgcolor: 'background.paper' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  The base URL of your LLM server (Ollama, vLLM, LM Studio, etc.)
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 3 }}>
                                 <FormControlLabel
                                   control={
                                     <Checkbox
                                       checked={config.supports_tools !== false}
                                       onChange={(e) => handleProviderChange(key, 'supports_tools', e.target.checked)}
                                       size="small"
                                     />
                                   }
                                   label={<Typography variant="body2">Function Calling (Tools)</Typography>}
                                 />
                                </Box>
                               <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1 }}>
                                 Multimodal (images) is set per-model in the Available Models list below.
                               </Typography>

                               <Box>
                                 <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                                   Routing Preferences (JSON — optional)
                                 </Typography>
                                 <TextField
                                   fullWidth
                                   size="small"
                                   multiline
                                   rows={6}
                                   value={config._raw_extra_body !== undefined
                                     ? config._raw_extra_body
                                     : (config.extra_body ? JSON.stringify(config.extra_body, null, 2) : '')}
                                   onChange={(e) => {
                                     const raw = e.target.value;
                                     if (!raw.trim()) {
                                       handleProviderChange(key, 'extra_body', null);
                                       handleProviderChange(key, '_raw_extra_body', '');
                                       return;
                                     }
                                     try {
                                       const parsed = JSON.parse(raw);
                                       if (parsed && typeof parsed === 'object') {
                                         handleProviderChange(key, 'extra_body', parsed);
                                       }
                                     } catch { /* keep typing — validation message below */ }
                                     handleProviderChange(key, '_raw_extra_body', raw);
                                   }}
                                   placeholder={'{"provider": {"data_collection": "deny", "sort": "throughput"}}'}
                                   sx={{ bgcolor: 'background.paper', '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                 />
                                 {(() => {
                                   const shown = config._raw_extra_body !== undefined
                                     ? config._raw_extra_body
                                     : (config.extra_body ? JSON.stringify(config.extra_body, null, 2) : '');
                                   let valid = true;
                                   if (shown.trim()) {
                                     try { const p = JSON.parse(shown); valid = !!p && typeof p === 'object'; } catch { valid = false; }
                                   }
                                   return (
                                     <Typography variant="caption" color={valid ? 'text.secondary' : 'error'}>
                                       {valid
                                         ? 'Merged verbatim into every request body. Used for OpenRouter routing: provider pinning (only), data policy (data_collection: "deny"), throughput sorting. Must be a JSON object.'
                                         : 'Invalid JSON — not saved until it parses.'}
                                     </Typography>
                                   );
                                 })()}
                               </Box>
                             </>
                           )}

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Default Model</Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={config.default_model || ''}
                              onChange={(e) => handleProviderChange(key, 'default_model', e.target.value)}
                              placeholder={config.provider === 'custom'
                                ? 'e.g., ollama/llama3, ollama/mistral, openai/gpt-4 (for Azure)'
                                : 'e.g., gpt-4o-mini, gemini-pro, claude-sonnet-4-5'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            {config.provider === 'custom' && (
                              <Typography variant="caption" color="text.secondary">
                                Format: provider/model-name (e.g., ollama/llama3, vllm/mistral-7b, azure/gpt-4)
                              </Typography>
                            )}
                          </Box>

                           <Box>
                             <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Available Models</Typography>
                             {/* Unified chip input for ALL providers — add any model manually */}
                             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', minHeight: 42 }}>
                                 {(config.available_models || []).map((model, idx) => {
                                   const isCustom = config.provider === 'custom';
                                   const isMm = (config.multimodal_models || []).some(p => {
                                     try { return new RegExp(p.replace(/\*/g, '.*'), 'i').test(model); }
                                     catch { return model.toLowerCase().includes(p.toLowerCase()); }
                                   });
                                   return (
                                     <Chip
                                       key={idx}
                                       label={model}
                                       size="small"
                                       onDelete={() => {
                                         const newModels = (config.available_models || []).filter((_, i) => i !== idx);
                                         handleProviderChange(key, 'available_models', newModels);
                                       }}
                                       onClick={isCustom ? () => {
                                         const mm = config.multimodal_models || [];
                                         if (isMm) {
                                           handleProviderChange(key, 'multimodal_models', mm.filter(p => {
                                             try { return !new RegExp(p.replace(/\*/g, '.*'), 'i').test(model); }
                                             catch { return !model.toLowerCase().includes(p.toLowerCase()); }
                                           }));
                                         } else {
                                           handleProviderChange(key, 'multimodal_models', [...mm, model]);
                                         }
                                       } : undefined}
                                       sx={{
                                         fontFamily: 'monospace', fontSize: '0.8rem',
                                         cursor: isCustom ? 'pointer' : 'default',
                                         border: isCustom && isMm ? '1.5px solid' : '1px solid',
                                         borderColor: isCustom && isMm ? 'success.main' : 'divider',
                                         bgcolor: isCustom && isMm ? 'success.main' : 'default',
                                         color: isCustom && isMm ? 'white' : 'default',
                                       }}
                                     />
                                   );
                                 })}
                                 <TextField
                                   size="small"
                                   variant="standard"
                                   placeholder={(config.available_models || []).length ? "Add model..." : "Type model name and press Enter (e.g. gemini-3.7-flash, gpt-5.7)"}
                                   onKeyDown={(e) => {
                                     if (e.key === 'Enter' || e.key === ',') {
                                       e.preventDefault();
                                       const value = e.target.value.trim();
                                       if (value && !(config.available_models || []).includes(value)) {
                                         handleProviderChange(key, 'available_models', [...(config.available_models || []), value]);
                                         e.target.value = '';
                                       }
                                     }
                                   }}
                                   sx={{
                                     flex: 1,
                                     minWidth: 150,
                                     '& .MuiInput-underline:before': { borderBottom: 'none' },
                                     '& .MuiInput-underline:hover:before': { borderBottom: 'none' },
                                     '& .MuiInput-underline:after': { borderBottom: 'none' },
                                   }}
                                   InputProps={{ disableUnderline: true }}
                                 />
                               </Box>
                               <Typography variant="caption" color="text.secondary">
                                 Type a model name and press Enter to add{config.provider === 'custom' ? '. Click a model to toggle multimodal (green).' : '.'}
                               </Typography>
                             </Box>
                           </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveLLMSettings}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save LLM Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Embedder Tab */}
        {activeTab === 5 && (
          <Card sx={tabWrapperSx}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Embedder Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure the dedicated embedding provider of this company. It is used for Knowledge
                  documents and for the personal Playbook (indexing and search), independently of
                  which LLM provider is used for chat.
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Knowledge documents and Playbook rules are indexed with this provider.
                  If you change the embedding provider or model, Knowledge documents must be re-uploaded
                  and Playbook rules re-indexed before search works correctly again.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={embeddingProvider.enabled}
                      onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                  }
                  label="Enable Embedder"
                />

                <FormControl fullWidth>
                  <InputLabel>Embedding Provider</InputLabel>
                  <Select
                    value={embeddingProvider.provider}
                    label="Embedding Provider"
                    onChange={(e) => {
                      const provider = e.target.value;
                      let defaultModel = 'text-embedding-3-small';
                      if (provider === 'gemini') defaultModel = 'gemini-embedding-2';
                      else if (provider === 'deepseek') defaultModel = 'deepseek-embedding-v3';
                      setEmbeddingProvider(prev => ({ ...prev, provider, model: defaultModel }));
                    }}
                  >
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Google Gemini</MenuItem>
                    <MenuItem value="deepseek">DeepSeek</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={embeddingProvider.api_key}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="API key for embedding provider"
                  helperText="This can be the same or different from your LLM provider API key"
                />

                <TextField
                  fullWidth
                  label="Embedding Model"
                  value={embeddingProvider.model}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, model: e.target.value }))}
                  placeholder={
                    embeddingProvider.provider === 'openai' ? 'text-embedding-3-small' :
                    embeddingProvider.provider === 'gemini' ? 'gemini-embedding-2' :
                    embeddingProvider.provider === 'deepseek' ? 'deepseek-embedding-v3' :
                    'Embedding model name'
                  }
                  helperText={
                    embeddingProvider.provider === 'openai' ? 'Options: text-embedding-3-small, text-embedding-3-large, text-embedding-ada-002' :
                    embeddingProvider.provider === 'gemini' ? 'Options: gemini-embedding-2, gemini-embedding-001' :
                    embeddingProvider.provider === 'deepseek' ? 'Options: deepseek-embedding-v3' :
                    'Enter the embedding model name'
                  }
                />

                <Button
                  variant="contained"
                  onClick={handleSaveEmbeddingSettings}
                  disabled={submitting}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save Embedder Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

      </Box>

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        companyId={currentUser?.company_id}
        currentUser={currentUser}
        onSuccess={() => loadInvitations(currentUser?.company_id)}
      />

      {/* Import Users from Excel Modal */}
      <ImportUsersModal
        open={importUsersModalOpen}
        onClose={() => setImportUsersModalOpen(false)}
        companyId={currentUser?.company_id}
        currentUser={currentUser}
        onSuccess={() => {
          loadUsers(currentUser?.company_id);
          loadInvitations(currentUser?.company_id);
        }}
      />

      {/* Resend invitation toast */}
      <Snackbar
        open={!!resendToast}
        autoHideDuration={4000}
        onClose={() => setResendToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={resendToast?.severity || 'info'} variant="filled" onClose={() => setResendToast(null)}>
          {resendToast?.message}
        </Alert>
      </Snackbar>

      {/* Per-user tag editor popover */}
      <UserTagsEditorPopover
        editor={tagEditor}
        users={users}
        catalog={userTags.map((t) => t.tag)}
        onClose={() => setTagEditor(null)}
        onSave={(userId, tags) => { handleSetUserTags(userId, tags); setTagEditor(null); }}
      />

      {/* Per-user assistants access popover */}
      <UserAccessPopover
        popover={accessPopover}
        users={users}
        allGpts={allGpts}
        onClose={() => setAccessPopover(null)}
        onRevoke={(gptId, userId) => handleRevokeGptAccess(gptId, userId)}
      />

      {/* Bulk actions dialog */}
      <BulkActionDialog
        dialog={bulkDialog}
        onClose={() => setBulkDialog(null)}
        selectedCount={selectedUserIds.size}
        companyGpts={companyGpts}
        userTags={userTags.map((t) => t.tag)}
        currentUser={currentUser}
        busy={bulkBusy}
        bulkGptIds={bulkGptIds}
        setBulkGptIds={setBulkGptIds}
        bulkRole={bulkRole}
        setBulkRole={setBulkRole}
        bulkTagsValue={bulkTagsValue}
        setBulkTagsValue={setBulkTagsValue}
        onConfirm={(action, payload) => handleBulkAction(action, payload)}
      />

    </Box>
  );
}

// Invite User Modal Component
function InviteUserModal({ open, onClose, companyId, currentUser, onSuccess }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [canCreateGpts, setCanCreateGpts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [availableGpts, setAvailableGpts] = useState([]);
  const [selectedGpts, setSelectedGpts] = useState([]);
  const [gptsLoading, setGptsLoading] = useState(false);

  // Check if current user can invite owners (only owners and super_admin can)
  const canInviteOwner = currentUser?.role === 'company_owner' || currentUser?.role === 'super_admin';

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('cme_token');
    setGptsLoading(true);
    fetch(`/companies/${currentUser?.company_id}/gpts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((gpts) => setAvailableGpts(Array.isArray(gpts) ? gpts : []))
      .catch(() => setAvailableGpts([]))
      .finally(() => setGptsLoading(false));
  }, [open]);

  const toggleGpt = (gptId) => {
    setSelectedGpts((prev) =>
      prev.includes(gptId) ? prev.filter((id) => id !== gptId) : [...prev, gptId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    const token = localStorage.getItem('cme_token');
    try {
      const res = await fetch(`/companies/${companyId}/invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role, can_create_gpts: role === 'user' ? canCreateGpts : true, shared_gpts: selectedGpts }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to send invitation');
      }

      const data = await res.json();

      if (data.email_sent) {
        setResult({ success: true, message: `Invitation sent to ${email}!` });
      } else {
        const fullUrl = `${window.location.origin}/accept-invite.html?token=${data.invitation.token}`;
        setResult({
          success: true,
          message: 'Email service not configured. Copy the link below:',
          link: fullUrl,
        });
        navigator.clipboard.writeText(fullUrl);
      }

      setEmail('');
      setRole('user');
      setCanCreateGpts(false);
      setSelectedGpts([]);
      onSuccess();
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Invite User
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            placeholder="user@example.com"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="company_admin">Company Admin</MenuItem>
              {canInviteOwner && <MenuItem value="company_owner">Company Owner</MenuItem>}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {role === 'company_owner'
              ? 'Company Owners can export conversations, manage admins and other owners'
              : role === 'company_admin'
              ? 'Company Admins can manage users, invitations, and company settings'
              : 'Users can use assistants assigned to them'}
          </Typography>

          {role === 'user' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={canCreateGpts}
                  onChange={(e) => setCanCreateGpts(e.target.checked)}
                />
              }
              label="Allow user to create assistants"
              sx={{ mt: 1, display: 'block' }}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share assistants (available once they register):
            </Typography>
            {gptsLoading ? (
              <CircularProgress size={20} />
            ) : availableGpts.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No assistants available to share.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedGpts.length === availableGpts.length && availableGpts.length > 0}
                        indeterminate={selectedGpts.length > 0 && selectedGpts.length < availableGpts.length}
                        onChange={(e) => setSelectedGpts(e.target.checked ? availableGpts.map((g) => g.id) : [])}
                      />
                    }
                    label={<Typography variant="caption">Select all</Typography>}
                  />
                  {selectedGpts.length > 0 && (
                    <Chip size="small" label={`${selectedGpts.length} selected`} />
                  )}
                </Box>
                <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5 }}>
                  {availableGpts.map((gpt) => (
                    <FormControlLabel
                      key={gpt.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedGpts.includes(gpt.id)}
                          onChange={() => toggleGpt(gpt.id)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{gpt.name || 'Untitled'}</Typography>
                          {gpt.is_shared && <Chip size="small" label="company" sx={{ height: 16, fontSize: 10 }} />}
                        </Box>
                      }
                      sx={{ display: 'flex', m: 0, px: 1, width: '100%' }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.message}
              {result.link && (
                <Box sx={{ mt: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {result.link}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}


// ── User tags editor popover ─────────────────────────────────────────────
function UserTagsEditorPopover({ editor, users, catalog, onClose, onSave }) {
  const [draft, setDraft] = useState([]);
  const [newTag, setNewTag] = useState('');
  const user = editor ? users.find((u) => u.id === editor.userId) : null;

  useEffect(() => {
    if (user) setDraft(user.tags || []);
  }, [editor?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor || !user) return null;

  const toggle = (tag) => setDraft((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  const addNew = () => {
    const n = newTag.trim().toLowerCase();
    if (n && !draft.includes(n)) setDraft((prev) => [...prev, n]);
    setNewTag('');
  };

  const allTags = Array.from(new Set([...catalog, ...draft])).sort();

  return (
    <Popover
      open
      anchorEl={editor.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, maxWidth: 340 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tags — {user.display_name || user.email}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {allTags.length === 0 && (
            <Typography variant="caption" color="text.secondary">No tags yet</Typography>
          )}
          {allTags.map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              color={draft.includes(t) ? 'primary' : 'default'}
              variant={draft.includes(t) ? 'filled' : 'outlined'}
              onClick={() => toggle(t)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="New tag (e.g. commercial)"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }}
            sx={{ flex: 1 }}
          />
          <Button size="small" onClick={addNew} disabled={!newTag.trim()}>Add</Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" variant="contained" onClick={() => onSave(user.id, draft)}>Save</Button>
        </Box>
      </Box>
    </Popover>
  );
}


// ── Per-user assistants access popover ───────────────────────────────────
function UserAccessPopover({ popover, users, allGpts, onClose, onRevoke }) {
  const user = popover ? users.find((u) => u.id === popover.userId) : null;
  if (!popover || !user) return null;

  const accessible = allGpts.filter(
    (g) => g.is_shared || g.user_id === user.id || (g.shared_with_users || []).includes(user.id)
  );

  return (
    <Popover
      open
      anchorEl={popover.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, minWidth: 340, maxWidth: 420 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Assistants — {user.display_name || user.email}
        </Typography>
        {accessible.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No assistants accessible</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 300, overflowY: 'auto' }}>
            {accessible.map((g) => {
              const kind = g.user_id === user.id
                ? { label: 'owner', color: 'primary', variant: 'filled' }
                : g.is_shared
                  ? { label: 'company', color: 'default', variant: 'outlined' }
                  : { label: 'shared', color: 'success', variant: 'outlined' };
              return (
                <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {g.name || 'Untitled'}
                  </Typography>
                  <Chip size="small" label={kind.label} color={kind.color} variant={kind.variant} sx={{ height: 22 }} />
                  {kind.label === 'shared' && (
                    <Tooltip title="Revoke access">
                      <IconButton size="small" onClick={() => onRevoke(g.id, user.id)} sx={{ p: 0.25 }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          owner = created by this user · company = shared with everyone · shared = assigned individually (✕ to revoke)
        </Typography>
      </Box>
    </Popover>
  );
}


// ── Bulk actions dialog ──────────────────────────────────────────────────
function BulkActionDialog({
  dialog, onClose, selectedCount, companyGpts, userTags, currentUser, busy,
  bulkGptIds, setBulkGptIds, bulkRole, setBulkRole, bulkTagsValue, setBulkTagsValue, onConfirm,
}) {
  if (!dialog) return null;
  const parseTags = () => bulkTagsValue.split(/[;,]/).map((t) => t.trim().toLowerCase()).filter(Boolean);

  const titles = {
    gpts: 'Assign GPTs',
    role: 'Change Role',
    addtags: 'Add Tags',
    removetags: 'Remove Tags',
    block: 'Block Users',
    unblock: 'Unblock Users',
    delete: 'Remove Users',
  };
  const canSetOwner = currentUser?.role === 'company_owner' || currentUser?.role === 'super_admin';

  const confirm = () => {
    if (dialog === 'gpts') onConfirm('assign_gpts', { gpt_ids: Array.from(bulkGptIds) });
    else if (dialog === 'role') onConfirm('set_role', { role: bulkRole });
    else if (dialog === 'addtags') onConfirm('add_tags', { tags: parseTags() });
    else if (dialog === 'removetags') onConfirm('remove_tags', { tags: parseTags() });
    else onConfirm(dialog);
  };

  const canConfirm =
    dialog === 'gpts' ? bulkGptIds.size > 0 :
    (dialog === 'addtags' || dialog === 'removetags') ? parseTags().length > 0 :
    true;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titles[dialog]} — {selectedCount} user{selectedCount !== 1 ? 's' : ''}</DialogTitle>
      <DialogContent>
        {dialog === 'gpts' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select the assistants to make available to all selected users:
            </Typography>
            {companyGpts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No assistants found in the company.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 320, overflowY: 'auto' }}>
                {companyGpts.map((gpt) => (
                  <FormControlLabel
                    key={gpt.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={bulkGptIds.has(gpt.id)}
                        onChange={() => setBulkGptIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(gpt.id)) next.delete(gpt.id); else next.add(gpt.id);
                          return next;
                        })}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{gpt.name || 'Untitled'}</Typography>
                        {gpt.is_shared && <Chip size="small" label="company" sx={{ height: 16, fontSize: 10 }} />}
                      </Box>
                    }
                    sx={{ m: 0, px: 1 }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
        {dialog === 'role' && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New role</InputLabel>
            <Select value={bulkRole} label="New role" onChange={(e) => setBulkRole(e.target.value)}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="company_admin">Company Admin</MenuItem>
              {canSetOwner && <MenuItem value="company_owner">Company Owner</MenuItem>}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Your own account and protected roles are skipped automatically.
            </Typography>
          </FormControl>
        )}
        {(dialog === 'addtags' || dialog === 'removetags') && (
          <>
            <TextField
              autoFocus
              fullWidth
              label="Tags (comma or semicolon separated)"
              placeholder={userTags.length ? `e.g. ${userTags.slice(0, 3).join(', ')}` : 'e.g. commercial, roma'}
              value={bulkTagsValue}
              onChange={(e) => setBulkTagsValue(e.target.value)}
              sx={{ mt: 1 }}
            />
            {userTags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                {userTags.map((t) => (
                  <Chip
                    key={t}
                    size="small"
                    label={t}
                    variant="outlined"
                    onClick={() => setBulkTagsValue((prev) => (prev ? `${prev}, ${t}` : t))}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
        {(dialog === 'block' || dialog === 'delete') && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {dialog === 'delete'
              ? `This will permanently remove ${selectedCount} user${selectedCount !== 1 ? 's' : ''}. Your own account is skipped. This action cannot be undone.`
              : `Blocked users cannot log in until unblocked (${selectedCount} selected).`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={confirm}
          variant="contained"
          color={dialog === 'delete' ? 'error' : 'primary'}
          disabled={busy || !canConfirm}
        >
          {busy ? <CircularProgress size={20} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ── Import users from Excel ──────────────────────────────────────────────
function ImportUsersModal({ open, onClose, companyId, currentUser, onSuccess }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [gpts, setGpts] = useState([]);
  const [selectedGpts, setSelectedGpts] = useState(() => new Set());
  const [role, setRole] = useState('user');
  const [tagsForAll, setTagsForAll] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const token = localStorage.getItem('cme_token');
  const canSetOwner = currentUser?.role === 'company_owner' || currentUser?.role === 'super_admin';
  const importable = rows ? rows.filter((r) => r.status === 'ok' || r.status === 'existing') : [];

  useEffect(() => {
    if (!open) {
      setFile(null); setRows(null); setCounts(null); setError(''); setResult(null);
      setSelectedGpts(new Set()); setRole('user'); setTagsForAll('');
    }
  }, [open]);

  useEffect(() => {
    if (open && companyId && gpts.length === 0) {
      fetch(`/companies/${companyId}/gpts`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : []))
        .then(setGpts)
        .catch(() => setGpts([]));
    }
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`/companies/${companyId}/import/users/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users-import-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Template download failed:', e);
    }
  };

  const handleFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setRows(null);
    setCounts(null);
    setError('');
    setResult(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`/companies/${companyId}/import/users/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to parse the file');
      } else {
        setRows(data.rows);
        setCounts(data.counts);
      }
    } catch (err) {
      setError('Failed to upload the file');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/companies/${companyId}/import/users/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rows: importable.map((r) => ({ email: r.email, name: r.name, tags: r.tags })),
          role,
          gpt_ids: Array.from(selectedGpts),
          tags_for_all: tagsForAll.split(/[;,]/).map((t) => t.trim().toLowerCase()).filter(Boolean),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Import failed');
      } else {
        setResult(data);
      }
    } catch (err) {
      setError('Import failed');
    } finally {
      setBusy(false);
    }
  };

  const statusChip = (r) => {
    if (r.status === 'ok') return <Chip size="small" color="success" label="will invite" />;
    if (r.status === 'existing') return <Chip size="small" color="info" label="exists → update" />;
    return <Chip size="small" color="error" label={r.reason || 'error'} />;
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import users from Excel</DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Import completed — {result.summary.invited} invited,{' '}
              {result.summary.existing_updated} updated, {result.summary.skipped} skipped
              {!result.email_sent && ' (email not configured: share the invite links below)'}
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 320, overflowY: 'auto' }}>
              {result.results.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    color={r.status === 'invited' ? 'success' : r.status === 'skipped' ? 'error' : 'info'}
                    label={r.status}
                  />
                  <Typography variant="body2">{r.email}</Typography>
                  {r.reason && (
                    <Typography variant="caption" color="text.secondary">({r.reason})</Typography>
                  )}
                  {r.accept_url && (
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {window.location.origin}{r.accept_url}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {/* Step 1 — template + upload */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                Download template
              </Button>
              <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                Upload .xlsx
                <input type="file" accept=".xlsx,.xlsm" hidden onChange={handleFileChosen} />
              </Button>
              {file && !busy && (
                <Typography variant="body2" color="text.secondary">{file.name}</Typography>
              )}
              {busy && <CircularProgress size={20} />}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Columns: <strong>email</strong> (required), <strong>name</strong>, <strong>tags</strong> ("commercial; roma").
              Role and assistants are chosen here below — once for everyone.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Preview table */}
            {rows && counts && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {counts.ok} to invite · {counts.existing} existing (will update) · {counts.error} with errors
                </Typography>
                <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {rows.map((r) => (
                    <Box
                      key={r.row}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      {statusChip(r)}
                      <Typography variant="body2" sx={{ minWidth: 180 }}>{r.email}</Typography>
                      <Typography variant="body2" color="text.secondary">{r.name}</Typography>
                      {(r.tags || []).map((t) => (
                        <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 20 }} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Step 2 — settings for everyone */}
            {importable.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl size="small" sx={{ maxWidth: 260 }}>
                  <InputLabel>Role for all</InputLabel>
                  <Select value={role} label="Role for all" onChange={(e) => setRole(e.target.value)}>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="company_admin">Company Admin</MenuItem>
                    {canSetOwner && <MenuItem value="company_owner">Company Owner</MenuItem>}
                  </Select>
                </FormControl>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Assistants for all</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 180, overflowY: 'auto' }}>
                    {gpts.map((gpt) => (
                      <FormControlLabel
                        key={gpt.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedGpts.has(gpt.id)}
                            onChange={() => setSelectedGpts((prev) => {
                              const next = new Set(prev);
                              if (next.has(gpt.id)) next.delete(gpt.id); else next.add(gpt.id);
                              return next;
                            })}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{gpt.name || 'Untitled'}</Typography>
                            {gpt.is_shared && <Chip size="small" label="company" sx={{ height: 16, fontSize: 10 }} />}
                          </Box>
                        }
                        sx={{ m: 0, px: 1 }}
                      />
                    ))}
                  </Box>
                </Box>
                <TextField
                  size="small"
                  label="Tags for all (comma separated)"
                  placeholder="e.g. commercial, roma"
                  value={tagsForAll}
                  onChange={(e) => setTagsForAll(e.target.value)}
                  sx={{ maxWidth: 380 }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={() => { onSuccess(); onClose(); }}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              variant="contained"
              onClick={apply}
              disabled={busy || importable.length === 0}
            >
              {busy ? <CircularProgress size={20} /> : `Import ${importable.length} user${importable.length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
